import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import { Box, Typography, Chip } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const InactivityIndicator = ({ isLoggedIn }) => {
  const { t } = useTranslation();
  const [inactiveTime, setInactiveTime] = useState(0);
  const [autoLogoutTimeout, setAutoLogoutTimeout] = useState(10); // Default 10 minutes

  useEffect(() => {
    if (!isLoggedIn) return;

    // Load autologout config
    const loadConfig = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE || `${window.location.protocol}//${window.location.hostname}${window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? ':25000' : ''}`;
        const response = await fetch(`${API_BASE}/config/autologout`);
        if (response.ok) {
          const data = await response.json();
          setAutoLogoutTimeout(data.autologout_minutes || 10);
        }
      } catch (error) {
        console.log('Could not load autologout config');
      }
    };

    loadConfig();

    // Update inactivity time every second
    const interval = setInterval(() => {
      const lastActivity = sessionStorage.getItem('lastActivity'); // KORRIGIERT: sessionStorage statt localStorage
      if (lastActivity) {
        const now = Date.now();
        const lastActivityTime = parseInt(lastActivity);
        const inactiveSeconds = Math.floor((now - lastActivityTime) / 1000);
        setInactiveTime(inactiveSeconds);
      } else {
        setInactiveTime(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLoggedIn]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Calculate warning levels with two-stage orange system - MEMOIZED to recalculate when autoLogoutTimeout changes
  const firstWarningThresholdSeconds = React.useMemo(() => Math.max(0, (autoLogoutTimeout - 2) * 60), [autoLogoutTimeout]); // 2 min vor Auto-Logout (outlined orange)
  const secondWarningThresholdSeconds = React.useMemo(() => Math.max(0, (autoLogoutTimeout - 1) * 60), [autoLogoutTimeout]); // 1 min vor Auto-Logout (filled orange)
  const logoutThresholdSeconds = React.useMemo(() => autoLogoutTimeout * 60, [autoLogoutTimeout]); // Auto-Logout Zeit (error/red)
  


  // Don't show if not logged in
  if (!isLoggedIn) return null;
  
  let chipColor = 'default';
  let chipVariant = 'outlined';
  
  if (inactiveTime >= logoutThresholdSeconds) {
    // Auto-Logout erreicht - ROT gefüllt
    chipColor = 'error';
    chipVariant = 'filled';
  } else if (inactiveTime >= secondWarningThresholdSeconds) {
    // 1 Minute vor Auto-Logout - ORANGE gefüllt
    chipColor = 'warning';
    chipVariant = 'filled';
  } else if (inactiveTime >= firstWarningThresholdSeconds) {
    // 2 Minuten vor Auto-Logout - ORANGE outlined
    chipColor = 'warning';
    chipVariant = 'outlined';
  } else if (inactiveTime > 60) {
    // Normale Inaktivität (über 1 Minute) - BLAU outlined
    chipColor = 'primary';
    chipVariant = 'outlined';
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 1000,
        opacity: 0.3,
        '&:hover': {
          opacity: 1
        }
      }}
    >
      <Chip
        icon={<AccessTime fontSize="small" />}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            
            <Typography variant="body2" component="span" fontWeight="bold">
              {formatTime(inactiveTime)}
            </Typography>
          </Box>
        }
        title={t('admin.passwordManagement.inactivity.tooltip')}
        color={chipColor}
        variant={chipVariant}
        size="small"
        sx={{
          fontSize: '0.75rem',
          height: 'auto',
          '& .MuiChip-label': {
            px: 1,
            py: 0.5
          }
        }}
      />
    </Box>
  );
};

export default InactivityIndicator;
